<template>
  <div class="body-measurements-list p-4">
    <RecordListPrintHeader title="身長体重管理" />

    <div
      v-for="member in members"
      :key="member.id"
      :data-member-id="member.id"
      class="body-measurements-list__user-row border mb-4">
      <div class="d-flex">
        <!-- ユーザー情報（左パネル） -->
        <div
          class="body-measurements-list__user-info body-measurements-list__user-info--clickable"
          @click="goToProfile(member.id)">
          <RecordListMemberInfo
            :member="member"
            :highlighted="member.id === highlightMemberId" />
        </div>

        <!-- 計測データ（右パネル） -->
        <div class="body-measurements-list__records-area">
          <div
            v-if="!member.bodyMeasurementGrades.length"
            class="fs-s text--grey05 p-3">
            身長体重データなし
          </div>
          <div
            v-for="grade in member.bodyMeasurementGrades"
            :key="grade.id"
            class="mb-3">
            <div class="body-measurements-list__grade-title fs-m mb-2">
              {{ grade.gradeCode | gradeCode }}
            </div>
            <table class="body-measurements-list__measurement-table">
              <thead>
                <tr>
                  <th class="body-measurements-list__row-label" />
                  <th
                    v-for="month in months"
                    :key="month">
                    {{ month }}月
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="body-measurements-list__row-label">
                    身長(cm)
                  </td>
                  <td
                    v-for="month in months"
                    :key="month">
                    {{ getMeasurementValue(grade, month, 'height') }}
                  </td>
                </tr>
                <tr>
                  <td class="body-measurements-list__row-label">
                    体重(kg)
                  </td>
                  <td
                    v-for="month in months"
                    :key="month">
                    {{ getMeasurementValue(grade, month, 'weight') }}
                  </td>
                </tr>
                <tr>
                  <td class="body-measurements-list__row-label">
                    BMI
                  </td>
                  <td
                    v-for="month in months"
                    :key="month">
                    {{ getMeasurementValue(grade, month, 'bmi') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RecordListPrintHeader from '@/components/view/records/RecordListPrintHeader.vue'
import RecordListMemberInfo from '@/components/view/records/RecordListMemberInfo.vue'
import { academicRecordGradeCodes } from '@/helper/profile'
import { buildMemberFilterApiParams, buildOrderingByFilterQuery, resolveFilterQuery } from '@/helper/records'

// 学校年度順（4〜12月 → 1〜3月）
const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]

export default {
  components: {
    RecordListPrintHeader,
    RecordListMemberInfo,
  },
  data() {
    return {
      members: [],
      highlightMemberId: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('recordProfile', ['filterParams']),
    months() {
      return MONTHS
    },
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      await this.fetchData()
    } finally {
      this.$store.commit('finishLoading')
    }
    this.$nextTick(() => {
      const memberId = this.$store.state.recordProfile.returnMemberId
      if (memberId) {
        this.$store.commit('recordProfile/clearReturnMemberId')
        this.highlightMemberId = memberId
        const el = document.querySelector(`[data-member-id="${memberId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    })
  },
  methods: {
    async fetchData() {
      const q = resolveFilterQuery(this.$route.query, this.filterParams)
      const memberParams = buildMemberFilterApiParams(q)
      const recordParams = {
        year_grade__in: q.body_grade || academicRecordGradeCodes.join(','),
        ...memberParams,
      }
      const [recordRes, usersRes] = await Promise.all([
        this.$axios.get('/admin/profiles/body-measurements/', { params: recordParams }),
        this.$axios.get('/users/', { params: { ...memberParams, ordering: buildOrderingByFilterQuery(q), limit: 999999 } }),
      ])
      const users = usersRes.data.results || []
      const recordMap = (recordRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.bodyMeasurementGrades
        return acc
      }, {})
      this.members = users.map(user => ({
        ...user,
        bodyMeasurementGrades: recordMap[user.id] || [],
      }))
    },
    getMeasurementValue(grade, month, field) {
      const measurement = grade.measurements.find(m => m.month === month)
      if (!measurement || measurement[field] == null) return '-'
      if (field === 'bmi') return measurement[field]
      return this.$options.filters.trimDecimalZero(measurement[field])
    },
    goToProfile(memberId) {
      this.$router.push({
        path: `/members/${memberId}/profile`,
        query: { backTo: 'recordList' },
      })
    },
  },
}
</script>

<style lang="scss">
@page {
  size: A4 landscape;
}
</style>

<style lang="scss" scoped>
.body-measurements-list {
  background-color: white;
  min-height: 100vh;

  &__user-row {
    page-break-inside: avoid;
  }

  &__user-info {
    width: 180px;
    flex-shrink: 0;
    padding: 12px;
    border-right: 1px solid $grey11;

    &--clickable {
      cursor: pointer;

      &:hover {
        background-color: $grey03;
      }
    }
  }

  &__records-area {
    flex: 1;
    padding: 12px;
    overflow-x: auto;
  }

  &__grade-title {
    border-bottom: 2px solid $l-blue;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }

  &__measurement-table {
    border-collapse: collapse;
    font-size: $fs-s;

    th,
    td {
      border: 1px solid $grey11;
      padding: 4px 8px;
      text-align: center;
      white-space: nowrap;
    }

    th {
      background-color: $grey03;
      font-weight: bold;
    }

    .body-measurements-list__row-label {
      text-align: left;
      background-color: $grey03;
      font-weight: bold;
    }
  }
}

@media (max-width: 767px) {
  .body-measurements-list {
    &__user-row > .d-flex {
      flex-direction: column;
    }

    &__user-info {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid $grey11;
    }

    &__measurement-table {
      th, td {
        padding: 3px 4px;
      }
    }
  }
}
</style>
